import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { OwnerBookingModal } from './owners/OwnerBookingModal';
import { StaffModal } from './staff/StaffModal';
import { ViewingModal } from './viewings/ViewingModal';
import { useStaff } from './staff/useStaff';
import { isoDate } from '../lib/dates';
import { signalMutation } from '../lib/mutationBus';

// The global "Create" host (app-shell-redesign AC5). The one New menu in the top
// bar opens a create from ANYWHERE — the modal floats over the current page, it
// doesn't navigate you to the owning page first. Mirrors AccountModalProvider
// (the one edit-user host): the shell mounts this once, any component calls
// openCreate(kind), and the matching create modal opens in place.
//
// The modals are the SAME components the pages use to edit — reused for create
// with their create flags (OwnerBookingModal.startNew / .createOwner; StaffModal
// .createHelper / .startNewLeave). A save signals the mutation bus, so every open
// page revalidates without a per-page reload wire. Dog-create is deliberately out
// of scope this build (no standalone flow yet — see the story's Out of scope).

export type CreateKind = 'booking' | 'owner' | 'helper' | 'leave' | 'viewing';

interface CreateModalApi {
  openCreate: (kind: CreateKind) => void;
}

const Ctx = createContext<CreateModalApi | null>(null);

// Read the opener. Null outside the provider (e.g. the staff-only shell), so a
// caller can hide its New affordance when there's nothing to open.
export function useCreateModal(): CreateModalApi | null {
  return useContext(Ctx);
}

export function CreateModalProvider({ children }: { children: ReactNode }) {
  const [kind, setKind] = useState<CreateKind | null>(null);
  const openCreate = useCallback((k: CreateKind) => setKind(k), []);
  const close = useCallback(() => setKind(null), []);
  // A create is a write whose domain we don't narrow here — bump the wildcard so
  // every open list/dashboard revalidates (the hooks default to the wildcard).
  const changed = useCallback(() => signalMutation(), []);
  const today = isoDate(new Date());

  return (
    <Ctx.Provider value={{ openCreate }}>
      {children}
      {(kind === 'booking' || kind === 'owner') && (
        <OwnerBookingModal
          startNew={kind === 'booking' || undefined}
          createOwner={kind === 'owner' || undefined}
          today={today}
          onClose={close}
          onChanged={changed}
        />
      )}
      {/* A viewing is logged from wherever you happen to be — most often on
          Bookings or Daily, with somebody on the telephone. It writes nothing
          but its own table, so the wildcard mutation signal is all any open
          page needs. */}
      {kind === 'viewing' && (
        <ViewingModal today={today} onClose={close} onSaved={changed} />
      )}
      {(kind === 'helper' || kind === 'leave') && (
        <StaffCreateHost kind={kind} today={today} onClose={close} onChanged={changed} />
      )}
    </Ctx.Provider>
  );
}

// StaffModal needs the staff list; fetch it lazily — only mounted while a helper/
// leave create is open, so a page that never opens one pays nothing. The modal
// opens the moment the list lands (instant when the cache is already warm).
function StaffCreateHost({
  kind,
  today,
  onClose,
  onChanged,
}: {
  kind: 'helper' | 'leave';
  today: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { state } = useStaff();
  if (state.phase !== 'ready') return null;
  return (
    <StaffModal
      staff={state.staff}
      today={today}
      createHelper={kind === 'helper' || undefined}
      startNewLeave={kind === 'leave' || undefined}
      onClose={onClose}
      onChanged={onChanged}
    />
  );
}
